import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { useState, useEffect, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { getDoc, type DocContent } from '~/lib/api'
import { useAppStore } from '~/lib/store'
import { DocsLayout } from '~/components/DocsLayout'
import { ApiReferenceReact } from '@scalar/api-reference-react'
import '@scalar/api-reference-react/style.css'

const MCP_ENDPOINT = 'https://legali-docs.pages.dev/api/mcp'

function generateDocFetchPrompt(path: string, title: string): string {
        return `Read the documentation "${title}" from Legali Docs via MCP.

Endpoint: ${MCP_ENDPOINT}
Resource URI: docs://${path}

JSON-RPC Request:

\`\`\`json
{
    "jsonrpc": "2.0",
    "id": 1,
    "method": "resources/read",
    "params": {
        "uri": "docs://${path}"
    }
}
\`\`\``
}

function CopyDocPromptButton({ path, title }: { path: string; title: string }) {
    const [copied, setCopied] = useState(false)

    const handleCopy = useCallback(async () => {
        const prompt = generateDocFetchPrompt(path, title)
        await navigator.clipboard.writeText(prompt)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }, [path, title])

    return (
        <button
            onClick={handleCopy}
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors ${
                copied
                    ? 'bg-green-600/20 text-green-400 border border-green-500/30'
                    : 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600'
            }`}
            title="Copy prompt to fetch this document"
        >
            {copied ? (
                <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                </>
            ) : (
                <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                    Copy Fetch Prompt
                </>
            )}
        </button>
    )
}

export const Route = createFileRoute('/docs/$')({
    component: DocViewerPage,
})

function DocViewerPage() {
    const params = useParams({ from: '/docs/$' })
    const path = params['_splat'] || ''
    const [doc, setDoc] = useState<DocContent | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const { docTree } = useAppStore()

    useEffect(() => {
        if (!path) {
            setLoading(false)
            return
        }

        setLoading(true)
        setError(null)

        getDoc(path)
            .then(setDoc)
            .catch((err: Error) => setError(err.message))
            .finally(() => setLoading(false))
    }, [path])

    // Get doc title from meta
    const getDocTitle = () => {
        if (!docTree) return null
        for (const section of docTree.sections) {
            for (const folder of section.folders) {
                const foundDoc = folder.children?.find(d => d.path === path)
                if (foundDoc) return { doc: foundDoc, folder, section }
            }
        }
        return null
    }

    const docMeta = getDocTitle()

    // Get tasks related to this doc
    // const relatedTasks = tasks.filter((t: Task) => t.docPath === path)

    if (loading) {
        return (
            <DocsLayout>
                <div className="p-6 animate-fade-in">
                    <div className="text-slate-400">Loading...</div>
                </div>
            </DocsLayout>
        )
    }

    if (error) {
        return (
            <DocsLayout>
                <div className="p-6 animate-fade-in">
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <h2 className="text-red-400 font-medium mb-2">Error loading document</h2>
                        <p className="text-red-300 text-sm">{error}</p>
                        <Link to="/docs" className="text-blue-400 text-sm mt-4 block hover:underline">
                            ← Back to docs
                        </Link>
                    </div>
                </div>
            </DocsLayout>
        )
    }

    if (!doc) {
        return (
            <DocsLayout>
                <div className="p-6 animate-fade-in">
                    <p className="text-slate-400">No document selected</p>
                    <Link to="/docs" className="text-blue-400 text-sm mt-2 block hover:underline">
                        ← Browse documentation
                    </Link>
                </div>
            </DocsLayout>
        )
    }

    return (
        <DocsLayout>
            <div className="animate-fade-in h-full flex flex-col">
                {/* Main content - scrollable container */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden">
                    <div className="p-6 max-w-4xl">
                        {/* Header with breadcrumbs and actions */}
                        <div className="flex items-start justify-between gap-4 mb-6">
                            {/* Breadcrumbs */}
                            <nav className="flex items-center gap-2 text-sm text-slate-400 flex-wrap">
                                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                                <span className="text-slate-600">›</span>
                                <Link to="/docs" className="hover:text-white transition-colors">Docs</Link>
                                {docMeta && (
                                    <>
                                        <span className="text-slate-600">›</span>
                                        <span className="text-slate-300">{docMeta.section.title}</span>
                                        <span className="text-slate-600">›</span>
                                        <span className="text-slate-300">{docMeta.folder.title}</span>
                                    </>
                                )}
                                <span className="text-slate-600">›</span>
                                <span className="text-white">{docMeta?.doc.title || doc.name.replace('.md', '')}</span>
                            </nav>

                            {/* Copy prompt button */}
                            <CopyDocPromptButton
                                path={path}
                                title={docMeta?.doc.title || doc.name.replace('.md', '')}
                            />
                        </div>

                        {/* Content Rendering */}
                        <div className="h-full">
                            {doc.name.endsWith('.yaml') || doc.name.endsWith('.yml') || doc.name.endsWith('.json') ? (
                                <div className="scalar-container -mx-6 h-full min-h-[800px]">
                                    <ApiReferenceReact
                                        configuration={{
                                            // @ts-ignore
                                            spec: {
                                                content: doc.content,
                                            },
                                            theme: 'none',
                                            isEditable: false,
                                            hideModels: false,
                                            hideTestRequestButton: false,
                                            darkMode: true,
                                        }}
                                    />
                                    <style>{`
                                        .scalar-container {
                                            --scalar-background-1: #0f172a;
                                            --scalar-background-2: #1e293b;
                                            --scalar-border-color: #334155;
                                            --scalar-color-1: #f8fafc;
                                            --scalar-color-2: #94a3b8;
                                            --scalar-color-3: #64748b;
                                            --scalar-color-accent: #3b82f6;
                                        }
                                        /* Hide the header if possible or adjust its position */
                                        .scalar-app header {
                                            display: none !important;
                                        }
                                        /* Ensure it fills the space */
                                        .scalar-app {
                                            height: 100% !important;
                                        }
                                    `}</style>
                                </div>
                            ) : (
                                <div className="markdown-body prose prose-invert max-w-none">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            // Custom checkbox rendering for task lists
                                            input: ({ type, checked, ...props }) => {
                                                if (type === 'checkbox') {
                                                    return (
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            className="task-checkbox"
                                                            readOnly
                                                            {...props}
                                                        />
                                                    )
                                                }
                                                return <input type={type} {...props} />
                                            },
                                            // Link handling
                                            a: ({ href, children }) => {
                                                if (href?.startsWith('./') || href?.startsWith('../')) {
                                                    // Relative doc link - use regular anchor
                                                    const basePath = path.split('/').slice(0, -1).join('/')
                                                    const targetPath = href.replace('./', `${basePath}/`).replace('.md', '')
                                                    return <a href={`/docs/${targetPath}`} className="text-blue-400 hover:underline">{children}</a>
                                                }
                                                return <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{children}</a>
                                            },
                                        }}
                                    >
                                        {doc.content}
                                    </ReactMarkdown>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </DocsLayout>
    )
}
